import type { AccessoryData } from '../blueprint-classes/accessories/types';
import type { ComponentData } from '../component-classes/components/types';
import type { CountertopSkuData } from '../blueprint-classes/countertops/types';
import type { IslandSkuData } from '../blueprint-classes/islands/types';
import { normalizeCatalogNumericsCfn } from './functions';
import type { PriceBookConstructorParams } from './types';
import type { PromotionalSkuAssetData } from '../blueprint-classes/promotional-skus/types';

/**
 * Immutable snapshot of the five catalog collections at one published version. Carried on
 * BlueprintContext so concurrent designs can resolve against different versions; a null
 * PriceBook leaves every read on the live singletons, which is today's behaviour.
 */
export class PriceBook {
	#versionId: string;
	#versionName: string;
	#publishedAt: string;
	#components: Record<string, ComponentData>;
	#islandSkus: Record<string, IslandSkuData>;
	#countertopSkus: Record<string, CountertopSkuData>;
	#accessories: Record<string, AccessoryData>;
	#promotions: Record<string, PromotionalSkuAssetData>;

	constructor (options: PriceBookConstructorParams) {
		const {
			versionId,
			versionName,
			publishedAt,
			components = {},
			islandSkus = {},
			countertopSkus = {},
			accessories = {},
			promotions = {}
		} = options;

		this.#versionId = versionId;
		this.#versionName = versionName;
		this.#publishedAt = publishedAt;
		this.#components = components;
		this.#islandSkus = normalizeCatalogNumericsCfn({ records: islandSkus, field: 'msrp' });
		this.#countertopSkus = normalizeCatalogNumericsCfn({ records: countertopSkus, field: 'msrp' });
		this.#accessories = normalizeCatalogNumericsCfn({ records: accessories, field: 'msrp' });
		this.#promotions = normalizeCatalogNumericsCfn({ records: promotions, field: 'priceAdjustment' });
	}

	get versionId (): string { return this.#versionId }
	get versionName (): string { return this.#versionName }
	get publishedAt (): string { return this.#publishedAt }
	get components (): Record<string, ComponentData> { return this.#components }

	public findIslandSku (sku: string): IslandSkuData | null { return this.#islandSkus[sku] ?? null }
	public findCountertopSku (sku: string): CountertopSkuData | null { return this.#countertopSkus[sku] ?? null }
	public findAccessory (sku: string): AccessoryData | null { return this.#accessories[sku] ?? null }
	public findPromotion (sku: string): PromotionalSkuAssetData | null { return this.#promotions[sku] ?? null }

	/** Component records in snapshot key order, ready for `new Components({ dataIn })` -- id/sku lookups stay on that instance (`context.components.findId`/`findSku`) rather than being re-implemented here */
	public toComponentsData (): Array<ComponentData> { return Object.values(this.#components) }
}
